/**
 * Animated favicon: smooth crossfade between the two real AyudaTecno logo
 * renderings — favicon-celeste.png (the canonical "resting" mark) and
 * favicon-cyan.png (the smiley-computer variant) — with a subtle scale "breath".
 *
 * Why JS canvas (not APNG/GIF): Chrome only renders the first frame of animated
 * favicons, so swapping a canvas-drawn data URL into <link rel="icon"> is the
 * only approach that animates in every modern browser.
 *
 * Cycle: hold celeste → fade to cyan → hold cyan → fade back. Celeste is the
 * dominant/resting state (the "correct" mark). Painting only happens during the
 * fade windows (holds reuse the last frame), the loop pauses while the tab is
 * hidden, and prefers-reduced-motion shows a static celeste mark.
 *
 * Sources live in /public; the static <link rel="icon" href="/favicon.ico"> in
 * index.html stays as the pre-JS fallback (also celeste).
 */

const SIZE = 64;
const SRCS = ['/favicon-celeste.png', '/favicon-cyan.png']; // [resting, accent]

// Timeline (ms): celeste is held longer so it reads as the primary identity.
const HOLD_A = 3000; // hold celeste
const FADE   = 750;  // crossfade duration (both directions)
const HOLD_B = 1300; // hold cyan
const CYCLE  = HOLD_A + FADE + HOLD_B + FADE;
const FPS    = 15;
const MIN_DELTA = 1000 / FPS;
const BREATH = 0.06; // max extra scale (6%) at the midpoint of a fade

let started = false;
let linkEl = null;
let canvas = null;
let ctx = null;
let imgs = [];
let rafId = null;
let lastPaint = 0;
let lastKey = null; // skip redundant toDataURL during holds

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function ensureLink() {
  if (linkEl && document.head.contains(linkEl)) return linkEl;
  linkEl = document.createElement('link');
  linkEl.rel = 'icon';
  linkEl.type = 'image/png';
  linkEl.setAttribute('sizes', '32x32');
  document.head.appendChild(linkEl);
  return linkEl;
}

// Returns { mix } where mix=0 → full celeste, mix=1 → full cyan, plus fade phase.
function phaseAt(t) {
  if (t < HOLD_A) return { mix: 0, fading: false };
  if (t < HOLD_A + FADE) return { mix: easeInOut((t - HOLD_A) / FADE), fading: true };
  if (t < HOLD_A + FADE + HOLD_B) return { mix: 1, fading: false };
  return { mix: 1 - easeInOut((t - HOLD_A - FADE - HOLD_B) / FADE), fading: true };
}

function drawImageCover(img, alpha, scale) {
  if (!img || !img.complete || !img.naturalWidth) return;
  ctx.globalAlpha = alpha;
  const s = SIZE * scale;
  const off = (SIZE - s) / 2;
  ctx.drawImage(img, off, off, s, s);
}

function paint(mix, scale) {
  // Quantize the key so holds (mix unchanged, scale==1) don't re-encode every frame.
  const key = `${mix.toFixed(3)}|${scale.toFixed(3)}`;
  if (key === lastKey) return;
  lastKey = key;

  ctx.clearRect(0, 0, SIZE, SIZE);
  drawImageCover(imgs[0], 1 - mix, scale);
  drawImageCover(imgs[1], mix, scale);
  ctx.globalAlpha = 1;
  ensureLink().href = canvas.toDataURL('image/png');
}

function frame(now) {
  rafId = requestAnimationFrame(frame);
  if (document.hidden) return;
  if (now - lastPaint < MIN_DELTA) return;
  lastPaint = now;

  const t = now % CYCLE;
  const { mix, fading } = phaseAt(t);
  // Gentle "breath": only during fades, peaking at the crossover.
  const scale = fading ? 1 + BREATH * Math.sin(Math.PI * (mix <= 0.5 ? mix * 2 : (1 - mix) * 2)) : 1;
  paint(mix, scale);
}

function startLoop() {
  if (rafId == null) rafId = requestAnimationFrame(frame);
}
function stopLoop() {
  if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
}

export function startAnimatedFavicon() {
  if (started || typeof document === 'undefined') return;
  started = true;

  canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  ctx = canvas.getContext('2d');

  let loaded = 0;
  let failed = false;
  imgs = SRCS.map((src) => {
    const im = new Image();
    im.onload = () => { if (++loaded === SRCS.length && !failed) onReady(); };
    im.onerror = () => { failed = true; }; // leave the static favicon.ico in place
    im.src = src;
    return im;
  });

  function onReady() {
    const reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) { paint(0, 1); return; } // static celeste

    startLoop();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopLoop();
      else { lastKey = null; startLoop(); }
    });
  }
}
